import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const subProcessors = [
  {
    name: 'Amazon Web Services EMEA SARL',
    purpose: 'Hosting, storage, CDN, lambda compute, identity (Cognito).',
    region: 'eu-north-1 (Stockholm, EU)',
    safeguard: 'UK adequacy regulations (EEA).',
  },
  {
    name: 'AWS Simple Email Service',
    purpose: 'Transactional email — booking confirmations, contact-form notifications, marketing emails where opted in.',
    region: 'eu-north-1 (Stockholm, EU)',
    safeguard: 'UK adequacy regulations (EEA).',
  },
  {
    name: 'Stripe Payments Europe Ltd',
    purpose: 'Payment processing for our subscription billing only.',
    region: 'EU / UK',
    safeguard: 'UK Addendum to EU SCCs for any onward transfer.',
  },
  {
    name: 'Google Ireland Ltd',
    purpose: 'Google Analytics 4 (consent-gated only) and Google Workspace operational email.',
    region: 'EU with onward transfers',
    safeguard: 'UK Addendum to EU SCCs.',
  },
  {
    name: 'Cloudflare Inc.',
    purpose: 'Where engaged on a client build — CDN, WAF, or DNS proxy services.',
    region: 'Global edge network',
    safeguard: 'UK Addendum to EU SCCs (Cloudflare DPA).',
  },
  {
    name: 'Domain registrars (Cloudflare, Namecheap, GoDaddy)',
    purpose: 'Domain registration where we manage the client\'s domain.',
    region: 'Varies by registrar',
    safeguard: 'Per registrar terms; UK Addendum where applicable.',
  },
];

const independentControllers = [
  {
    name: 'Stripe Payments Europe Ltd / Stripe, Inc.',
    purpose: 'For Commerce-tier online stores, Stripe acts as an independent controller (and in some flows joint controller with the client) of the connected account\'s customer payment data, processing it for fraud prevention, anti-money-laundering, regulatory and tax obligations as a regulated payment institution.',
    reference: (
      <>See{' '}
        <a href="https://stripe.com/gb/legal/dpa" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
          Stripe&apos;s DPA
        </a>.
      </>
    ),
  },
  {
    name: 'Google LLC',
    purpose: 'Google reCAPTCHA, where enabled on a client\'s public forms (anti-bot protection).',
    reference: null,
  },
];

const SubProcessors = () => {
  return (
    <div data-testid="sub-processors-page">
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-primary mb-6">
              Sub-processors
            </h1>
            <p className="text-muted-foreground mb-8">
              Last updated: 1 May 2026
            </p>

            <div className="prose prose-slate max-w-none space-y-8">
              <section>
                <p className="text-muted-foreground">
                  This page lists the third parties we engage to deliver our Website-as-a-Service to clients,
                  and which process personal data on our behalf. It mirrors Schedule B of our Service Agreement.
                  We update this page whenever we add or replace a sub-processor and notify clients in line with
                  our Service Agreement (clause 15).
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
                  Sub-processors of client personal data
                </h2>
                <p className="text-muted-foreground mb-4">
                  Each of the following processes personal data on our documented instructions, under written
                  data-protection terms no less protective than our Service Agreement.
                </p>
                <div className="space-y-5">
                  {subProcessors.map((p) => (
                    <div key={p.name} className="border border-border/40 rounded-lg p-5">
                      <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{p.name}</h3>
                      <dl className="text-sm text-muted-foreground space-y-1">
                        <div>
                          <dt className="inline font-semibold text-foreground">Purpose: </dt>
                          <dd className="inline">{p.purpose}</dd>
                        </div>
                        <div>
                          <dt className="inline font-semibold text-foreground">Region: </dt>
                          <dd className="inline">{p.region}</dd>
                        </div>
                        <div>
                          <dt className="inline font-semibold text-foreground">Transfer safeguard: </dt>
                          <dd className="inline">{p.safeguard}</dd>
                        </div>
                      </dl>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
                  Independent controllers
                </h2>
                <p className="text-muted-foreground mb-4">
                  These third parties receive personal data through our platform but determine the purposes and
                  means of their own processing. They are not our sub-processors.
                </p>
                <div className="space-y-5">
                  {independentControllers.map((c) => (
                    <div key={c.name} className="border border-border/40 rounded-lg p-5">
                      <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{c.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {c.purpose}{' '}
                        {c.reference}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
                  Negative declaration
                </h2>
                <p className="text-muted-foreground">
                  As at the date above, we do not engage third-party error-tracking services
                  (e.g. Sentry, Datadog, Bugsnag, Rollbar, LogRocket), customer-support tools
                  (e.g. Intercom, HubSpot, Zendesk), or anti-bot providers other than Google reCAPTCHA
                  (where enabled), in respect of client personal data. This page is updated if that changes.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Notice of changes</h2>
                <p className="text-muted-foreground">
                  Where we propose to add or replace a sub-processor that processes client personal data,
                  we give clients at least 30 days&apos; written notice and a right to object on reasonable
                  data-protection grounds. If a reasonable objection cannot be resolved within 30 days,
                  the client may terminate the affected services without further fee. Full terms in our
                  Service Agreement.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">Questions</h2>
                <p className="text-muted-foreground">
                  Email{' '}
                  <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">
                    support@docksidedesign.co.uk
                  </a>{' '}
                  with any questions about our sub-processors or the safeguards we apply to international transfers.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SubProcessors;
